import React, { useEffect, useState, useMemo } from "react";
import {
  ArrowLeftOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  message,
  Spin,
} from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "store/actions";
import { debounce } from "lodash";

const EditVisicooler = (props) => {
  const { visicoolerId } = useParams();
  const breadcrumbItems = [
    { title: "Visicooler Listing", link: "#" },
    { title: "Edit Visicooler", link: "#" },
  ];

  const [form] = Form.useForm();

  // States
  const [states, setStates] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [fetchingCity, setFetchingCity] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const navigate = useNavigate();

  // Fetch token from localStorage
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;

  // Fetch employee options
  const fetchEmployeeOptions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const options = response.data.map(emp => ({
        label: emp.employeeName,
        value: emp._id
      }));
      setEmployeeOptions(options);
    } catch (error) {
      console.error("Error fetching employee options:", error);
      message.error("Failed to load employee options");
    }
  };

  useEffect(() => {
    fetchEmployeeOptions();
    // Hardcoded list of Indian States
    const stateList = [
      "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
      "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli", "Daman and Diu", "Delhi", "Goa",
      "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
      "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
      "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
      "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
    ];
    setStates(stateList.map(s => ({ label: s, value: s })));
  }, []);

  useEffect(() => {
    props.setBreadcrumbItems("Visicoolers", breadcrumbItems);
  }, []);

  // Fetch visicooler data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASEURL}/visicoolers/${visicoolerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        // Set form fields
        form.setFieldsValue({
          ...data,
          visicoolerCapacity: Number(data.visicoolerCapacity),
          requiredVisicooler: Number(data.requiredVisicooler),
          monthlySaleInCases: Number(data.monthlySaleInCases),
          requestedBy: data.requestedBy?._id || data.requestedBy || undefined,
        });

        // Set state/city dropdowns
        setSelectedState(data.state);

        // Populate cityOptions with the existing city so it displays correctly
        if (data.city) {
          setCityOptions([{ label: data.city, value: data.city }]);
        }

      } catch (error) {
        message.error("Failed to fetch visicooler details");
      }
      setLoading(false);
    };
    fetchData();
  }, [visicoolerId]);

  // Photon Autocomplete Logic
  const fetchCityList = async (value) => {
    if (!value) return;
    setFetchingCity(true);
    setCityOptions([]);
    try {
      const query = `${value}, ${selectedState}`;
      const res = await axios.get(`https://photon.komoot.io/api/`, {
        params: { q: query, limit: 10, lang: 'en' }
      });

      const features = res.data.features || [];
      const options = features.map(item => {
        const props = item.properties;
        const name = props.name;
        const placeState = props.state || '';
        const district = props.district || props.city || '';
        const label = `${name}${district ? `, ${district}` : ''}${placeState ? `, ${placeState}` : ''}`;
        return { label, value: name };
      });

      const uniqueOptions = [...new Map(options.map(item => [item.label, item])).values()];
      setCityOptions(uniqueOptions);

    } catch (e) {
      console.error(e);
    } finally {
      setFetchingCity(false);
    }
  };

  const debouncedFetchCity = useMemo(() => debounce(fetchCityList, 400), [selectedState]);

  const handleStateChange = (value) => {
    setSelectedState(value);
    setCityOptions([]);
    form.setFieldsValue({ city: undefined });
  };

  const onFinish = async (values) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASEURL}/visicoolers/${visicoolerId}`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Visicooler updated successfully");
      navigate("/visicoolers");
    } catch (error) {
      console.error(error);
      message.error("Failed to update visicooler");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <Card title="Edit Visicooler">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Outlet Name"
                name="outletName"
                rules={[{ required: true, message: "Outlet Name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Servicing DB Name"
                name="servicingDBName"
                rules={[{ required: true, message: "Servicing DB Name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Owner Name"
                name="ownerName"
                rules={[{ required: true, message: "Owner Name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Tracking ID"
                name="trackingId"
                rules={[{ required: true, message: "Tracking ID is required" }]}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Mobile Number"
                name="contactNumber"
                rules={[{ required: true, message: "Mobile Number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Email ID"
                name="email"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Invalid email" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Aadhar Number"
                name="aadharNumber"
                rules={[{ required: true, message: "Aadhar Number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="GST Number"
                name="gstNumber"
                rules={[{ required: true, message: "GST Number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="PAN Number"
                name="panNumber"
                rules={[{ required: true, message: "PAN Number is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="State"
                name="state"
                rules={[{ required: true, message: "State is required" }]}
              >
                <Select
                  showSearch
                  options={states}
                  onChange={handleStateChange}
                  placeholder="Select State"
                  allowClear
                  filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="City (Type to search)"
                name="city"
                rules={[{ required: true, message: "City is required" }]}
              >
                <Select
                  showSearch
                  options={cityOptions}
                  onSearch={debouncedFetchCity}
                  placeholder={selectedState ? "Start typing to find City..." : "Select State First"}
                  disabled={!selectedState}
                  allowClear
                  filterOption={false}
                  notFoundContent={fetchingCity ? <Spin size="small" /> : "Type to search..."}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Outlet Type"
                name="outletType"
                rules={[{ required: true, message: "Outlet Type is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Visicooler Capacity (in litres)"
                name="visicoolerCapacity"
                rules={[{ required: true, message: "Visicooler Capacity is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Visicoolers Required"
                name="requiredVisicooler"
                rules={[{ required: true, message: "Required Visicooler is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Monthly Sales (in cases)"
                name="monthlySaleInCases"
                rules={[{ required: true, message: "Monthly Sales is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Address of Outlet"
                name="outletAddress"
                rules={[{ required: true, message: "Address is required" }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Requested By"
                name="requestedBy"
                rules={[{ required: true, message: "Requester is required" }]}
              >
                <Select
                  showSearch
                  options={employeeOptions}
                  placeholder="Select Employee"
                  allowClear
                  filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                style={{
                  backgroundColor: PRIMARY_RED,
                  borderColor: PRIMARY_RED,
                  marginRight: 12,
                }}
              >
                Update Visicooler
              </Button>
            </Col>
            <Col>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/visicoolers")}
                style={{ color: PRIMARY_RED }}
              >
                Back
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(EditVisicooler);
